/**
 * Presence Badge
 *
 * The small "someone else is editing this" indicator shared by PresenceInput,
 * PresenceSelect, PresenceTextarea and PartLookupInput: an initials circle,
 * colour-hashed from the editor's user id, that expands on hover to show the
 * full name (Google Docs style).
 *
 * Two deliberate layout properties, both of which we previously got wrong:
 *
 * 1. The badge is a single absolutely-positioned element with NO in-flow
 *    wrapper. It used to be wrapped in the Radix <Tooltip>, whose trigger is a
 *    static `inline-flex` span — an in-flow inline box that generated a line
 *    box and grew the field's container every time a badge appeared. That was
 *    the source of the page jumping.
 * 2. It expands leftward. The right edge is pinned (`right-2`), so growing the
 *    width moves only the left edge and the name never runs off the field.
 *
 * Showing the name on hover in-place also avoids the tooltip's anchoring
 * problem: a portalled tooltip anchored to that same zero-width span rendered
 * underneath the field rather than beside the initials.
 */

import * as React from "react"
import { cn } from "../../lib/utils"

// Hex, not rgb(): the ring below composes an alpha suffix onto these, which is
// only valid on hex. `rgb(59, 130, 246)20` is not a colour and CSS drops the
// whole declaration silently.
const PRESENCE_COLORS = [
  '#3b82f6', // blue
  '#22c55e', // green
  '#fb923c', // orange
  '#a855f7', // purple
  '#ec4899', // pink
  '#0ea5e9', // sky
  '#eab308', // yellow
  '#ef4444', // red
]

// Consistent colour per user id (same user = same colour everywhere, every session)
export function getPresenceColor(userId) {
  if (!userId) return 'var(--primary)'

  let hash = 0
  for (let i = 0; i < userId.length; i++) {
    hash = userId.charCodeAt(i) + ((hash << 5) - hash)
  }

  return PRESENCE_COLORS[Math.abs(hash) % PRESENCE_COLORS.length]
}

/**
 * Inline style marking a field as being edited by someone else.
 *
 * The ring is a box-shadow, never a border. These fields are filled controls
 * (`bg-input`) that carry no `border` utility, so Tailwind's preflight leaves
 * them at `border-width: 0` — setting `borderColor` paints nothing at all.
 * box-shadow also renders outside the border box, so it costs zero layout and
 * the field cannot resize when presence appears or clears.
 */
export function getPresenceFieldStyle(userId) {
  const color = getPresenceColor(userId)
  // The CSS-variable fallback can't take an alpha suffix, so it gets no glow.
  const glow = color.startsWith('#') ? `, 0 0 0 5px ${color}33` : ''
  return { boxShadow: `0 0 0 2px ${color}${glow}` }
}

const PresenceBadge = ({ editor, className }) => {
  if (!editor) return null

  const color = getPresenceColor(editor.user_id)
  const initials = editor.user_initials || editor.user_name?.slice(0, 2).toUpperCase() || '?'

  return (
    <div
      className={cn(
        "group/presence absolute -top-2 right-2 z-20 flex h-5 items-center overflow-hidden rounded-full text-white shadow-sm cursor-default",
        "animate-in fade-in slide-in-from-top-1 duration-200",
        className
      )}
      style={{ backgroundColor: color }}
      aria-label={`${editor.user_name} is editing this field`}
    >
      <span className="grid h-5 w-5 shrink-0 place-items-center text-[10px] font-semibold">
        {initials}
      </span>
      <span
        className={cn(
          "max-w-0 whitespace-nowrap text-[10px] font-medium",
          "transition-[max-width,padding] duration-200 ease-out",
          "group-hover/presence:max-w-48 group-hover/presence:pr-2"
        )}
      >
        {editor.user_name}
      </span>
    </div>
  )
}

export { PresenceBadge }
export default PresenceBadge
